import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import 'react-datepicker/dist/react-datepicker.css';

const parseDate = (dateString) => {
  const cleanedDateString = dateString.trim();
  return new Date(`${cleanedDateString}T00:00:00Z`);
};

function ProbeGeneration() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [isLoadingModalVisible, setIsLoadingModalVisible] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const fetchUserId = async () => {
    try {
      const token = localStorage.getItem('access');
      if (!token) throw new Error('No token found. Please log in again.');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/users/me/`, {
        headers: { Authorization: `JWT ${token}`, 'Content-Type': 'application/json' },
      });
      return response.data.id;
    } catch (error) {
      console.error('Error fetching user ID:', error);
      throw error;
    }
  };

  const fetchAvailableDatabases = async () => {
    try {
      const userId = await fetchUserId();
      const response = await axios.get(
        `https://9nn9frhaq8.execute-api.us-west-2.amazonaws.com/dev/dates?user_id=${userId}`
      );
      // console.log('Response from Lambda:', response.data);
      
      if (response.data && response.data.available_databases) {
        setAvailableDatabases(response.data.available_databases);
      } else {
        console.warn('No databases returned from Lambda.');
      }
    } catch (error) {
      console.error('Error fetching available databases:', error);
    }
  };

  const fetchAvailableDates = async (db) => {
    setStatusMessage('Please wait, fetching available dates...');
    try {
        const userId = await fetchUserId(); // Ensure this returns the correct user ID
        const apiUrl = `https://9nn9frhaq8.execute-api.us-west-2.amazonaws.com/dev/dates?user_id=${userId}&db=${db}`;
        // console.log('Fetching dates from:', apiUrl);
        const response = await axios.get(apiUrl);
        // console.log('Response from Lambda:', response.data);

        if (response.data && response.data.dates) {
          const parsedDates = response.data.dates.map(parseDate); // Apply date parsing
          // console.log("Parsed dates for date picker:", parsedDates);
          setAvailableDates(parsedDates); // Update state
          setStatusMessage('✅ Available dates loaded'); 
      } else {
          console.warn('No dates returned from Lambda.');
          setStatusMessage('No dates found.'); 
      }
      
    } catch (error) {
        console.error('Error fetching available dates:', error);
        setStatusMessage('Error fetching dates. Please try again.');
    }
  };

  const handleDatabaseChange = async (event) => {
    const selectedDb = event.target.value; // Selected database from dropdown
    // console.log('Selected database:', selectedDb); // Debug log
    setSelectedDb(selectedDb);
    setStartDate(null);
    setEndDate(null);

    // Extract and display only the relevant part of the database name
    const displayDbName = selectedDb.replace(/^.*\/(.*?)\.db$/, '$1'); // Extract file name without parent folder and extension
    // console.log('Display database name:', displayDbName); // Debug log

    if (selectedDb) {
      await fetchAvailableDates(selectedDb); // Fetch dates for selected database
    }
  };

  useEffect(() => {
    fetchAvailableDatabases();
  }, []);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (endDate && date > endDate) {
      setErrorModalMessage('The start date must be earlier than the end date.');
      setIsErrorModalVisible(true);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (startDate && date < startDate) {
      setErrorModalMessage('The end date must be later than the start date.');
      setIsErrorModalVisible(true);
    }
  };

  const handleGeneratePlot = async () => {
    if (!startDate || !endDate) {
      setErrorModalMessage('Both start and end dates are required.');
      setIsErrorModalVisible(true);
      return;
    }
  
    const userId = await fetchUserId();
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);
  
    // Check if selectedDb is set
    if (!selectedDb) {
      setErrorModalMessage('Please select a database.');
      setIsErrorModalVisible(true);
      return;
    }
  
    const payload = {
      user_id: userId,
      selected_db: selectedDb, // Add the selected database to the payload
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };
  
    // console.log('Payload for Lambda:', payload);
    setIsLoadingModalVisible(true);
  
    try {
      const response = await axios.post(
        'https://9nn9frhaq8.execute-api.us-west-2.amazonaws.com/dev/dates',
        payload
      );
  
      if (response.data && response.data.plot_urls) {
        const plotUrls = response.data.plot_urls;
        const images = Object.entries(plotUrls)
          .map(
            ([sensor, url]) =>
              `<img src="${url}" alt="Sensor ${sensor} Plot" style="width: 100%; height: auto; margin-bottom: 10px;"/>`
          )
          .join('');
        setModalMessage(`<br/>${images}`);
        setIsModalVisible(true);
      } else {
        console.warn("Unexpected API Response Format:", response.data);
        setErrorModalMessage('Graph generation succeeded, but no URLs were returned.');
        setIsErrorModalVisible(true);
      }
    } catch (error) {
      console.error("Error while generating graph:", error);
      setErrorModalMessage('Failed to generate the graph. Please try again.');
      setIsErrorModalVisible(true);
    } finally {
      setIsLoadingModalVisible(false);
    }
  };
  
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '420px',  // Fixed height (readjust this as needed)
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: '20px',
    padding: '20px',
    textAlign: 'center',
  };  

  const titleStyle = {
    fontSize: '36px',  // fixed size
    fontWeight: '500',
    color: '#000000',
    marginBottom: '30px',
    whiteSpace: 'nowrap',  // Prevents text from wrapping
  };  

  const buttonStyle = {
    backgroundColor: '#0d6efd',
    color: 'white',
    padding: '12px 24px',
    fontSize: '18px',
    borderRadius: '5px',
    cursor: 'pointer',
    margin: '10px 0',
    transition: 'background-color 0.3s ease',
  };

  const generatePlotButtonStyle = {
    ...buttonStyle,
    backgroundColor: startDate && endDate ? '#4CAF50' : '#A9A9A9',
    cursor: startDate && endDate ? 'pointer' : 'not-allowed',
    marginTop: '30px',
    padding: '14px 28px',
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Soil Probe Graphs</h1>
      <h6 style={{ marginBottom: '20px' }}>
        Select a System, enter Start and End Dates, then press the Button below
      </h6>
      <select 
        onChange={handleDatabaseChange} 
        defaultValue="" 
        style={{
          fontSize: '16px', 
          padding: '10px', 
          width: '300px', 
          borderRadius: '5px', 
          textAlign: 'center', 
          cursor: 'pointer', 
        }}
      >
        {/* Placeholder option */}
        <option value="" disabled>
          Select System
        </option>

        {/* Map through available databases */}
        {availableDatabases.map((db, index) => {
          // Extract the filename from the full path and remove the `.db` extension
          const displayDbName = db.replace(/^.*\/(.*?)\.db$/, '$1');
          
          return (
            <option key={index} value={db}>
              {displayDbName}
            </option>
          );
        })}
      </select>
      {statusMessage && (
        <p
          style={{
            textAlign: 'center',
            color: statusMessage.includes('Please wait') ? 'gray' : 
                  statusMessage.includes('loaded') ? 'green' : 
                  statusMessage.includes('Error') ? 'red' : 'black', 
            marginTop: '10px'
          }}
        >
          {statusMessage}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '8px',
          marginTop: '20px',
          flexDirection: window.innerWidth < 480 ? 'column' : 'row',
          alignItems: 'center',
        }}
      >
        <DatePicker
          selected={startDate}
          onChange={handleStartDateChange}
          placeholderText="Enter Start Date"
          dateFormat="dd/MM/yyyy"
          highlightDates={availableDates}
          includeDates={availableDates}
          style={{ width: '100%' }}
        />
        <DatePicker
          selected={endDate}
          onChange={handleEndDateChange}
          placeholderText="Enter End Date"
          dateFormat="dd/MM/yyyy"
          minDate={startDate}
          highlightDates={availableDates}
          includeDates={availableDates}
          style={{ width: '100%' }}
        />
      </div>

      <button
        style={generatePlotButtonStyle}
        onClick={handleGeneratePlot}
        disabled={!startDate || !endDate}
      >
        Generate
      </button>

      {/* Modals for loading, success, and error */}
      {isLoadingModalVisible && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block' }} aria-labelledby="loadingModalLabel" aria-hidden="true">
          <div className="modal-dialog" style={{ marginTop: '100px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="loadingModalLabel">Please Wait</h5>
              </div>
              <div className="modal-body">
                Your graph will be ready soon...
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalVisible && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block' }} aria-labelledby="modalLabel" aria-hidden="true">
          <div className="modal-dialog" style={{ marginTop: '100px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="modalLabel">Graphs Generated Successfully!</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setIsModalVisible(false)}></button>
              </div>
              <div className="modal-body" dangerouslySetInnerHTML={{ __html: modalMessage }} />
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" data-bs-dismiss="modal" onClick={() => setIsModalVisible(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isErrorModalVisible && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block' }} aria-labelledby="errorModalLabel" aria-hidden="true">
          <div className="modal-dialog" style={{ marginTop: '100px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="errorModalLabel">Error</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setIsErrorModalVisible(false)}></button>
              </div>
              <div className="modal-body">
                {errorModalMessage}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={() => setIsErrorModalVisible(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProbeGeneration;
